import { createAsyncThunk } from "@reduxjs/toolkit";

import { ACTIVITY_TYPES } from "Constants/constants";
import logger from "Libs/logger";
import { loadEnvironments } from "Reducers/environment";
import { gitProjectSelector } from "Reducers/project/project";
import { AsyncThunkOptionType } from "Reducers/types";
import { AppDispatch } from "Store/configureStore";

import { getProjectActivitiesRequest } from "Api/activity/getProjectActivities.request";
import { getActivityTypes } from "../activity.slice";
import { LoadProjectActivities } from "../types";

import type { Project } from "@packages/client";
import type { Activity } from "Api/activity/types";

const checkDeletedEnvironments = (
  dispatch: AppDispatch,
  activities: Activity[],
  organizationId: string,
  project: Project
) => {
  if (
    activities.some(
      activity => activity.type === ACTIVITY_TYPES.ENVIRONMENT.DELETE
    )
  ) {
    dispatch(
      loadEnvironments({
        organizationId,
        projectId: project?.id
      })
    );
  }
};

export const loadProjectActivities = createAsyncThunk<
  {
    activities: Activity[];
    hasMore: boolean;
    organizationDescriptionId: string;
    projectDescriptionId: string;
    appName?: string;
  },
  LoadProjectActivities,
  AsyncThunkOptionType
>(
  "app/activity/project/load",
  async (
    { projectDescriptionId, organizationDescriptionId, filterNames, appName },
    { dispatch, getState, rejectWithValue }
  ) => {
    const filters = getActivityTypes(filterNames);
    // Don't make any request when no filter is selected

    if (!filters.length) {
      return {
        activities: [],
        hasMore: false,
        organizationDescriptionId,
        projectDescriptionId
      };
    }

    const project = gitProjectSelector(getState(), {
      organizationId: organizationDescriptionId,
      projectId: projectDescriptionId
    });
    try {
      const activities = await getProjectActivitiesRequest({
        projectId: projectDescriptionId,
        filters
      });

      let hasMore = false;
      if (activities.length > 9) {
        hasMore = true;
        activities.pop();
      }

      if (project) {
        checkDeletedEnvironments(
          dispatch,
          activities,
          organizationDescriptionId,
          project
        );
      }

      const filteredActivities = activities.filter(
        activity => activity.parameters.application === appName
      );

      return {
        activities: appName ? filteredActivities : activities,
        hasMore,
        organizationDescriptionId,
        projectDescriptionId
      };
    } catch (error: any) {
      if (![404, 403, 400].includes(error.code ?? error.status ?? 0)) {
        logger(error, {
          action: "app/activity/project/load_failure",
          meta: {
            organizationDescriptionId,
            projectDescriptionId
          }
        });
      }

      return rejectWithValue(error);
    }
  }
);
